import React, {useEffect, useMemo} from 'react';
import {Divider, Radio, Spin} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import Box from '../../../common/Layout/Box';
import HStack from '../../../common/Layout/HStack';
import VStack from '../../../common/Layout/VStack';
import AppText from '../../../common/AppText';
import FormInput from '../../../common/FormInput';
import AdvanceSelect from '../../../common/AdvanceSelect';
import SimpleInput from '../../../common/SimpleInput';
import CustomButtonV2 from '../../../common/CustomButtonV2';
import {fetchApi} from '../../../../services/HTTP';
import {t} from '../../../../services/i18n';
import {getCategoriesByTypes} from '../../../../redux/ScheduleSlice';
import {APPOINTMENT} from '../../../../services/ConstHelper';
import {isMobile} from '../../../../services/Helpers';
import {Colors} from '../../../../styles/Colors';
import {GlobalStyleAttributes} from '../../../../styles/GlobalStyles';
import {GlobalConsts} from '../../../../styles/GlobalConsts';

const MODE_ALL = 'all';
const MODE_PER_SERVICE = 'per_service';
const SERVICE_WIDTH = isMobile ? undefined : '220px';
const PRICE_WIDTH = '112px';
const TRASH_WIDTH = '2.25rem';
const ROW_GAP = isMobile ? '0.75rem' : '0.625rem';
const INPUT_STYLE = {height: '38px', width: '100%'};

const getBranchServices = (eventTypes, locationId) => (eventTypes?.[APPOINTMENT] ?? []).map((category) => {
    const membershipType = category.membership_types?.find((mt) => mt.location_box_fk === locationId);
    return membershipType && {membership_types_id: membershipType.id, name: category.name, price: membershipType.price};
}).filter(Boolean);

const buildBranch = (services, savedById) => {
    const allIds = services.map((service) => service.membership_types_id);
    const saved = services.filter((service) => savedById[service.membership_types_id]);
    const subsidies = _.uniq(saved.map((service) => Number(savedById[service.membership_types_id].subsidy)));
    const isAll = saved.length === services.length && subsidies.length <= 1 && saved.every((service) => _.isNil(savedById[service.membership_types_id].frequency));
    if (isAll) return {mode: MODE_ALL, allSubsidy: subsidies[0] ?? '', rows: [], allIds};
    const rows = saved.map((service) => ({...service, subsidy: savedById[service.membership_types_id].subsidy, union_price: savedById[service.membership_types_id].frequency ?? ''}));
    return {mode: MODE_PER_SERVICE, allSubsidy: '', rows, allIds};
};

const isEmpty = (value) => _.isNil(value) || value === '';

const OrganizationPriceList = ({values, setFieldValue, errors, submitCount}) => {
    const dispatch = useDispatch();
    const box = useSelector((state) => state.box.box);
    const eventTypes = useSelector((state) => state.schedule.eventTypes.data);
    const currencySymbol = box.locations_box[0]?.currency_symbol;
    const locationOptions = useMemo(() => box.locations_box.map((location) => ({value: location.id, label: location.location})), [box]);
    const branch = values.branches?.[values.locationId];
    const branchServices = useMemo(() => getBranchServices(eventTypes, values.locationId), [eventTypes, values.locationId]);
    const serviceOptions = useMemo(() => branchServices.filter((service) => !branch?.rows.some((row) => row.membership_types_id === service.membership_types_id)).map((service) => ({label: service.name, value: service.membership_types_id})), [branchServices, branch?.rows]);
    const showErrors = submitCount > 0 && !!errors?.branches;
    const headerFontSize = GlobalStyleAttributes.fonts.infoText;
    const bodyFontSize = GlobalStyleAttributes.fonts.standard16;

    useEffect(() => {
        if (!eventTypes) dispatch(getCategoriesByTypes(false));
    }, [eventTypes]);

    useEffect(() => {
        if (!eventTypes || values.branches) return;
        const loadSavedPriceList = async () => {
            const saved = await fetchApi(`organizations/${values.usersBoxesId}/priceList`, 'GET', null, false, true);
            const savedById = _.keyBy(saved, 'membership_type_fk');
            setFieldValue('branches', Object.fromEntries(locationOptions.map(({value}) => [value, buildBranch(getBranchServices(eventTypes, value), savedById)])));
            setFieldValue('locationId', locationOptions[0].value);
        };
        loadSavedPriceList();
    }, [eventTypes]);

    if (!branch) return <Spin/>;

    const setBranch = (patch) => setFieldValue('branches', {...values.branches, [values.locationId]: {...branch, ...patch}});
    const addRow = (membershipTypesId) => setBranch({rows: [...branch.rows, {...branchServices.find((service) => service.membership_types_id === membershipTypesId), subsidy: '', union_price: ''}]});
    const updateRow = (membershipTypesId, field, value) => setBranch({rows: branch.rows.map((row) => (row.membership_types_id === membershipTypesId ? {...row, [field]: value} : row))});
    const removeRow = (membershipTypesId) => setBranch({rows: branch.rows.filter((row) => row.membership_types_id !== membershipTypesId)});

    return (
        <VStack space={'1.5rem'} width={'100%'}>
            {locationOptions.length > 1 && (
                <FormInput label={t('location')} width={'100%'} containerProps={{mb: 0}}>
                    <AdvanceSelect dontSort options={locationOptions} initValue={values.locationId} onChange={(option) => setFieldValue('locationId', option.value)}/>
                </FormInput>
            )}
            <Radio.Group value={branch.mode} onChange={(e) => setBranch({mode: e.target.value})}>
                <VStack space={'0.75rem'}>
                    <Radio value={MODE_ALL}><AppText fontSize={bodyFontSize}>{t('organization-price-list-all-services')}</AppText></Radio>
                    {branch.mode === MODE_ALL && (
                        <Box style={{paddingInlineStart: '1.75rem'}}>
                            <SimpleInput value={branch.allSubsidy} onChangeCallback={(value) => setBranch({allSubsidy: value})} numericOnly width={PRICE_WIDTH} formatAddon={currencySymbol} styleOverride={INPUT_STYLE} status={showErrors && isEmpty(branch.allSubsidy) ? 'error' : null}/>
                        </Box>
                    )}
                    <Radio value={MODE_PER_SERVICE}><AppText fontSize={bodyFontSize}>{t('organization-price-list-per-service')}</AppText></Radio>
                </VStack>
            </Radio.Group>
            {showErrors && <AppText fontSize={headerFontSize} color={Colors.error}>{t('thisFieldIsMandatory')}</AppText>}
            {branch.mode === MODE_PER_SERVICE && (
                <VStack space={'1rem'} width={'100%'}>
                    {serviceOptions.length > 0 && <AdvanceSelect options={serviceOptions} initValue={null} onChange={(option) => addRow(option.value)} placeholder={t('serviceTypeLbl')} hideCreateBtn/>}
                    {branch.rows.length > 0 && (
                        <VStack width={'100%'}>
                            <HStack width={'100%'} alignItems={'flex-end'} pb={'0.35rem'} space={ROW_GAP}>
                                <Box width={SERVICE_WIDTH} flex={isMobile ? 1 : undefined}><AppText fontSize={headerFontSize} mediumFont>{t('serviceTypeLbl')}</AppText></Box>
                                <Box width={PRICE_WIDTH} flexShrink={0}><AppText fontSize={headerFontSize} mediumFont>{t('organization-price-list-subsidy-of')}</AppText></Box>
                                <Box width={PRICE_WIDTH} flexShrink={0}><AppText fontSize={headerFontSize} mediumFont>{t('organization-price-list-from-treatment-price')}</AppText></Box>
                                <Box width={TRASH_WIDTH} flexShrink={0}/>
                            </HStack>
                            <Divider style={{margin: 0, background: Colors.borderColor}}/>
                            {branch.rows.map((row) => (
                                <HStack key={row.membership_types_id} width={'100%'} alignItems={'center'} py={'0.75rem'} space={ROW_GAP} borderBottomWidth={1} borderBottomColor={Colors.borderColor} style={{borderBottomStyle: 'solid'}}>
                                    <Box width={SERVICE_WIDTH} flex={isMobile ? 1 : undefined} minW={0}><AppText fontSize={bodyFontSize} numberOfLines={2}>{row.name}</AppText></Box>
                                    <Box width={PRICE_WIDTH} flexShrink={0}>
                                        <SimpleInput value={row.subsidy} onChangeCallback={(value) => updateRow(row.membership_types_id, 'subsidy', value)} numericOnly width={'100%'} formatAddon={currencySymbol} styleOverride={INPUT_STYLE} status={showErrors && isEmpty(row.subsidy) ? 'error' : null}/>
                                    </Box>
                                    <Box width={PRICE_WIDTH} flexShrink={0}>
                                        <SimpleInput value={row.union_price} onChangeCallback={(value) => updateRow(row.membership_types_id, 'union_price', value)} numericOnly width={'100%'} formatAddon={currencySymbol} placeholder={row.price?.toString()} styleOverride={INPUT_STYLE}/>
                                    </Box>
                                    <Box width={TRASH_WIDTH} flexShrink={0} justifyContent={'center'}>
                                        <CustomButtonV2 type={'link'} iconName={'fa-trash'} onClick={() => removeRow(row.membership_types_id)} iconProps={{iconSize: GlobalConsts.iconSize.XS}}/>
                                    </Box>
                                </HStack>
                            ))}
                        </VStack>
                    )}
                </VStack>
            )}
        </VStack>
    );
};

export default OrganizationPriceList;
